import { ChatMessage } from '@bosmart/types';
import { QuestionAnswer, ConversationMemoryState } from './types';

/**
 * ConversationMemory service for storing and retrieving conversation history
 * and Q&A pairs during interviews.
 * 
 * This enables RAG-like functionality by maintaining conversation context
 * that can be injected into the AI's context when needed.
 */
export class ConversationMemory {
  private history: ChatMessage[] = [];
  private questionAnswers: QuestionAnswer[] = [];
  private lastActivityAt: Date = new Date();

  /**
   * Add a message to conversation history
   */
  addMessage(message: ChatMessage): void {
    this.history.push(message);
    this.lastActivityAt = new Date();
  }

  /**
   * Add a Q&A pair extracted from conversation
   */
  addQuestionAnswer(questionId: string, questionText: string, answerText: string): void {
    this.questionAnswers.push({
      questionId,
      questionText,
      answerText,
      timestamp: new Date().toISOString(),
    });
    this.lastActivityAt = new Date();
  }

  /**
   * Get recent conversation summary (last N exchanges)
   */
  getRecentSummary(limit: number = 5): string {
    const recent = this.history.slice(-limit);
    if (recent.length === 0) {
      return 'No recent conversation history.';
    }

    return recent
      .map((msg) => {
        const role = msg.role === 'user' ? 'Candidate' : 'AI Interviewer';
        return `${role}: ${msg.text}`;
      })
      .join('\n');
  }

  /**
   * Get all Q&A pairs as a map
   */
  getQuestionAnswers(): Map<string, QuestionAnswer> {
    const map = new Map<string, QuestionAnswer>();
    this.questionAnswers.forEach((qa) => {
      map.set(qa.questionId, qa);
    });
    return map;
  }

  /**
   * Get formatted Q&A summary for context injection
   */
  getQuestionAnswerSummary(): string {
    if (this.questionAnswers.length === 0) {
      return 'No questions have been answered yet.';
    }

    return this.questionAnswers
      .map((qa, index) => {
        return `Question ${index + 1} (ID: ${qa.questionId}):\n  Q: ${qa.questionText}\n  A: ${qa.answerText}`;
      })
      .join('\n\n');
  }

  /**
   * Get full conversation history
   */
  getHistory(): ChatMessage[] {
    return [...this.history];
  }

  /**
   * Get current memory state
   */
  getState(): ConversationMemoryState {
    return {
      history: [...this.history],
      questionAnswers: [...this.questionAnswers],
      lastActivityAt: new Date(this.lastActivityAt),
    };
  }

  /**
   * Clear all memory (useful for testing or reset)
   */
  clear(): void {
    this.history = [];
    this.questionAnswers = [];
    this.lastActivityAt = new Date();
  }

  /**
   * Get last activity timestamp
   */
  getLastActivityAt(): Date {
    return new Date(this.lastActivityAt);
  }

  /**
   * Check if memory is empty
   */
  isEmpty(): boolean {
    return this.history.length === 0 && this.questionAnswers.length === 0;
  }
}

